import { axiom } from '../../lib/axiom';
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Bot } from 'lucide-react';
import { useChatSession } from './hooks/useChatSession';
import { useStreams } from '../../contexts/StreamsContext';
import { ToolCall } from '../../hooks/useWorkflowChat';
import type { NodeStatus } from '../../types/api';
import { TimelineView } from './TimelineView';
import { ChatHeader } from './ChatHeader';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { ApprovalDialogs } from './ApprovalDialogs';
import { LoadingIndicator } from './LoadingIndicator';
import { ProgressCard } from './ProgressCard';
import { StreamingMessage } from './StreamingMessage';
import { useGlassBoxEvents } from '../../hooks/useGlassBoxEvents';
import { useProgressSteps } from '../../hooks/useProgressSteps';
import { AgentLoopError } from './AgentLoopError';
import { BotChatHeader } from './BotChatHeader';
import { BotChatMessages } from './BotChatMessages';
import { BotChatInput } from './BotChatInput';

export type ChatVariant = 'inline' | 'floating';

interface ChatPanelBaseProps {
  streamId: string;
  streamName?: string;
  onClose: () => void;
}

interface TaskChatPanelProps extends ChatPanelBaseProps {
  scope: 'task';
  variant: 'inline';
  nodeId: string;
  taskName: string;
  taskStatus: string;
  agentName?: string;
  onShowDetails?: () => void;
  onShowContext?: () => void;
}

interface StreamChatPanelProps extends ChatPanelBaseProps {
  scope: 'stream';
  variant: 'floating';
  context?: 'stream' | 'global';
  nodeId?: never;
  taskName?: never;
  taskStatus?: never;
  agentName?: never;
  onShowDetails?: never;
  onShowContext?: never;
}

export type ChatPanelProps = TaskChatPanelProps | StreamChatPanelProps;

/**
 * Unified chat panel component that handles both task-level and stream-level chat.
 * Uses scope to determine which UI variant to render.
 */
export function ChatPanel(props: ChatPanelProps) {
  const { scope, streamId } = props;

  const nodeId = scope === 'task' ? props.nodeId : undefined;

  // Use the unified chat session hook
  const session = useChatSession({
    streamId,
    nodeId,
    scope,
    persistDrafts: scope === 'task',
  });

  // Stream context for additional operations
  const { updateNode } = useStreams();

  // Render task-scoped inline chat
  if (scope === 'task') {
    return (
      <TaskChatPanel
        {...props}
        session={session}
        updateNode={updateNode}
      />
    );
  }

  // Render stream-scoped floating chat
  return (
    <StreamChatPanel
      {...props}
      session={session}
    />
  );
}

// ============================================================================
// Task Chat Panel (inline variant)
// ============================================================================

interface TaskChatPanelInternalProps extends TaskChatPanelProps {
  session: ReturnType<typeof useChatSession>;
  updateNode: (nodeId: string, updates: { status: NodeStatus }) => Promise<void>;
}

function TaskChatPanel({
  streamId,
  streamName = '',
  nodeId,
  taskName,
  taskStatus,
  agentName = 'Claude',
  onClose,
  onShowDetails,
  onShowContext,
  session,
  updateNode,
}: TaskChatPanelInternalProps) {
  // Spec 167 Phase 3: Progress card — derive steps from node-scoped Glass Box events
  const { eventsForNode } = useGlassBoxEvents(streamId);
  const nodeEvents = eventsForNode(nodeId);
  const progressSteps = useProgressSteps(nodeEvents);

  const viewModeKey = `viewMode_${nodeId}`;
  const [viewMode, setViewMode] = useState<'chat' | 'timeline'>(() => {
    try {
      const saved = localStorage.getItem(viewModeKey);
      return saved === 'timeline' ? 'timeline' : 'chat';
    } catch {
      return 'chat';
    }
  });

  // Persist viewMode when it changes
  useEffect(() => {
    try {
      localStorage.setItem(viewModeKey, viewMode);
    } catch {
      // Ignore localStorage errors
    }
  }, [viewMode, viewModeKey]);

  // Reset viewMode when switching tasks
  useEffect(() => {
    try {
      const saved = localStorage.getItem(viewModeKey);
      setViewMode(saved === 'timeline' ? 'timeline' : 'chat');
    } catch {
      setViewMode('chat');
    }
  }, [viewModeKey]);

  // Helper to format tool call for timeline display
  const formatToolForTimeline = (toolCall: ToolCall): { name: string; description: string } => {
    switch (toolCall.name) {
      case 'get_stream_graph':
        return { name: 'get_stream_graph', description: 'Read workflow graph' };
      case 'create_node': {
        const name = (toolCall.input as { name?: string }).name || 'task';
        return { name: 'create_node', description: `Created task "${name}"` };
      }
      case 'update_node_status': {
        const status = (toolCall.input as { status?: string }).status || 'unknown';
        return { name: 'update_node_status', description: `Changed status to ${status}` };
      }
      case 'begin_waiting':
        return { name: 'begin_waiting', description: 'Started waiting for subtasks' };
      case 'record_decision':
        return { name: 'record_decision', description: 'Recorded decision' };
      case 'complete_node':
        return { name: 'complete_node', description: 'Completed task' };
      default:
        return { name: toolCall.name, description: toolCall.name };
    }
  };

  // Transform messages into timeline sections
  const timelineSections = useMemo(() => {
    const sections: Array<{
      id: string;
      timestamp: string;
      userPrompt: string;
      agentResponses: Array<{
        id: string;
        content: string;
        model: string;
        toolUse?: Array<{ name: string; description: string }>;
      }>;
      status: 'success' | 'error' | 'pending';
      hasCode: boolean;
    }> = [];

    let currentSection: typeof sections[0] | null = null;

    session.messages.forEach((msg) => {
      if (msg.role === 'user') {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          id: msg.id,
          timestamp: msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          userPrompt: msg.content,
          agentResponses: [],
          status: 'pending',
          hasCode: false,
        };
      } else if (msg.role === 'assistant' && currentSection) {
        const toolUse = msg.toolCalls?.map(formatToolForTimeline);
        currentSection.agentResponses.push({
          id: msg.id,
          content: msg.content,
          model: agentName || 'Claude',
          toolUse: toolUse && toolUse.length > 0 ? toolUse : undefined,
        });
        currentSection.status = 'success';
        if (msg.content.includes('```')) {
          currentSection.hasCode = true;
        }
      }
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return sections;
  }, [session.messages, agentName]);

  // Mark task as completed
  const handleMarkComplete = async () => {
    try {
      await updateNode(nodeId, { status: 'completed' as NodeStatus });
    } catch (err) {
      axiom.error('Failed to mark task complete', { error: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div className="h-full flex flex-col bg-surface-900 overflow-hidden">
      {/* Header */}
      <ChatHeader
        taskName={taskName}
        taskStatus={taskStatus}
        streamName={streamName}
        sessionTokens={session.sessionTokens}
        autoApprove={session.autoApprove}
        viewMode={viewMode}
        onClose={onClose}
        onShowDetails={onShowDetails}
        onShowContext={onShowContext}
        onSetAutoApprove={session.setAutoApprove}
        onSetViewMode={setViewMode}
        onMarkComplete={handleMarkComplete}
      />

      {/* Content Area - Chat or Timeline */}
      {viewMode === 'timeline' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          {session.isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
            </div>
          ) : (
            <TimelineView sections={timelineSections} />
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {session.isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
            </div>
          ) : session.messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bot className="w-10 h-10 text-text-muted mb-3" />
              <p className="text-text-muted text-sm">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <>
              <ChatMessageList
                messages={session.messages.filter(m => m.role !== 'system')}
                isLoading={session.isLoading}
                onEditMessage={session.handleEditMessage}
              />
              <ApprovalDialogs
                pendingApproval={session.pendingApproval}
                pendingNodeName={session.pendingNodeName}
                rateLimitApproval={session.rateLimitApproval}
                onToolApproval={session.handleToolApproval}
                onToolRejection={session.handleToolRejection}
                onRateLimitApproval={session.handleRateLimitApproval}
              />
              {session.agentError && (
                <AgentLoopError
                  error={session.agentError}
                  onRetry={session.lastUserMessage ? session.handleRetryError : undefined}
                  onDismiss={session.handleDismissError}
                />
              )}
              {session.isLooping && (session.streamingContent || session.streamingThinking) ? (
                <StreamingMessage
                  content={session.streamingContent}
                  thinking={session.streamingThinking}
                  onStop={session.stopAgentLoop}
                />
              ) : session.isLooping ? (
                progressSteps.length > 0 ? (
                  <ProgressCard
                    steps={progressSteps}
                    onStop={session.stopAgentLoop}
                  />
                ) : (
                  <LoadingIndicator
                    loopingStatus={session.loopingStatus ?? undefined}
                    onStop={session.stopAgentLoop}
                  />
                )
              ) : null}
              <div ref={session.messagesEndRef} />
            </>
          )}
        </div>
      )}

      {/* Input */}
      <ChatInput
        value={session.input}
        onChange={session.setInput}
        onSend={session.handleSend}
        isDisabled={!session.input.trim() || session.isLoading}
        inputRef={session.inputRef}
      />
    </div>
  );
}

// ============================================================================
// Stream Chat Panel (floating variant)
// ============================================================================

interface StreamChatPanelInternalProps extends StreamChatPanelProps {
  session: ReturnType<typeof useChatSession>;
}

function StreamChatPanel({
  streamId,
  streamName,
  onClose,
  context = 'stream',
  session,
}: StreamChatPanelInternalProps) {
  const suggestedQuestions = context === 'stream'
    ? [
        'What needs my attention?',
        'Summarize recent progress',
        'What\'s blocking this stream?',
      ]
    : [
        'What needs my attention across all streams?',
        'Which streams are at risk?',
        'Give me a daily summary',
      ];

  const handleSuggestedQuestionClick = (question: string) => {
    session.setInput(question);
    session.inputRef.current?.focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-20 right-6 w-96 h-[500px] bg-surface-900 border border-surface-600 rounded-xl shadow-2xl flex flex-col z-50 overflow-hidden"
    >
      <BotChatHeader
        context={context}
        streamName={streamName}
        streamId={streamId}
        sessionTokens={session.sessionTokens}
        autoApprove={session.autoApprove}
        onToggleAutoApprove={() => session.setAutoApprove(!session.autoApprove)}
        onClose={onClose}
      />

      <BotChatMessages
        messages={session.messages
          .filter(m => m.role !== 'system')
          .map(m => ({ ...m, role: m.role as 'user' | 'assistant' | 'tool_use' | 'tool_result' }))}
        isLoadingHistory={session.isLoadingHistory}
        isLoading={session.isLoading}
        loopingStatus={session.loopingStatus ?? undefined}
        pendingApproval={session.pendingApproval}
        pendingNodeName={session.pendingNodeName}
        rateLimitApproval={session.rateLimitApproval}
        agentError={session.agentError}
        context={context}
        streamName={streamName}
        suggestedQuestions={suggestedQuestions}
        onApprove={session.handleToolApproval}
        onReject={session.handleToolRejection}
        onRateLimitApproval={session.handleRateLimitApproval}
        onSuggestedQuestionClick={handleSuggestedQuestionClick}
        onRetryError={session.handleRetryError}
        onDismissError={session.handleDismissError}
        streamingContent={session.streamingContent}
        streamingThinking={session.streamingThinking}
        messagesEndRef={session.messagesEndRef}
      />

      <BotChatInput
        value={session.input}
        disabled={session.isLoading}
        onChange={session.setInput}
        onSend={session.handleSend}
        inputRef={session.inputRef}
      />
    </motion.div>
  );
}
